import React, { useMemo } from 'react';
import { Tag, Tooltip, Empty } from 'antd';

// By-day weekly view of a partner's recurring schedules (OVO-172-adjacent —
// "on which sites do we go each day?"). Rows = sites, columns = weekdays
// (Mon→Sun). A cell shows every passage that touches the site that weekday:
// green "→ X" when the site is the pickup origin, blue "← X" when it is the
// delivery destination, with the time window when set. Built from the recurring
// PATTERN (the OrderSchedule weekday mask) — the same every week. Read-only;
// shared by the admin Recurring tab and the partner-viewer one.

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DAY_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function RecurringWeeklyGrid({ schedules = [], showPickups = true, showDeliveries = true }) {
  const { sites, cells, dayCounts } = useMemo(() => {
    const active = (schedules || []).filter((s) => s && s.isActive !== false);
    const siteMap = new Map();
    const cells = new Map(); // `${siteId}|${day}` -> passages[]
    const dayCounts = Array(7).fill(0);
    const seenSite = (s) => {
      if (s && s.id != null && !siteMap.has(s.id)) siteMap.set(s.id, s);
    };
    const push = (siteId, day, p) => {
      const k = `${siteId}|${day}`;
      if (!cells.has(k)) cells.set(k, []);
      cells.get(k).push(p);
    };
    for (const sc of active) {
      const wd = sc.weekdays || [];
      const s = sc.deliveryTimeStart;
      const e = sc.deliveryTimeEnd;
      const win = s && e ? `${s}–${e}` : s ? `${s}–…` : e ? `…–${e}` : null;
      // Hide/unhide pickups vs deliveries — only the visible side registers its
      // site, so e.g. "pickups only" shows just the pickup sites.
      const out = showPickups && sc.pickupSite?.id != null;
      const inb = showDeliveries && sc.deliverySite?.id != null;
      if (!out && !inb) continue;
      if (out) seenSite(sc.pickupSite);
      if (inb) seenSite(sc.deliverySite);
      for (let d = 0; d < 7; d++) {
        if (!wd[d]) continue;
        dayCounts[d] += 1;
        if (out) push(sc.pickupSite.id, d, { dir: 'out', other: sc.deliverySite?.name || '—', window: win });
        if (inb) push(sc.deliverySite.id, d, { dir: 'in', other: sc.pickupSite?.name || '—', window: win });
      }
    }
    const sites = [...siteMap.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { sites, cells, dayCounts };
  }, [schedules, showPickups, showDeliveries]);

  if (!sites.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No active recurring schedules to show"
        style={{ padding: 32 }}
      />
    );
  }

  const th = {
    padding: '8px 10px',
    textAlign: 'center',
    fontSize: 12,
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
  };
  const td = { padding: '6px 8px', verticalAlign: 'top', borderBottom: '1px solid #f3f3f3', minWidth: 130 };
  const sticky = { position: 'sticky', left: 0, background: '#fff', zIndex: 1, boxShadow: '1px 0 0 #eee' };

  return (
    <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 820 }}>
        <thead>
          <tr>
            <th style={{ ...th, ...sticky, textAlign: 'left', minWidth: 210, color: '#555' }}>
              Site <span style={{ color: '#bbb', fontWeight: 400 }}>({sites.length})</span>
            </th>
            {DAY_LABELS.map((lbl, d) => (
              <th key={d} style={{ ...th, background: d >= 5 ? '#fafafa' : undefined }}>
                <Tooltip title={DAY_FULL[d]}>
                  <div style={{ fontWeight: 700, color: d >= 5 ? '#bbb' : '#333' }}>{lbl}</div>
                </Tooltip>
                <div style={{ fontSize: 10, color: '#bbb' }}>{dayCounts[d] || ''}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => (
            <tr key={site.id}>
              <td style={{ ...td, ...sticky, fontWeight: 600, fontSize: 13 }}>{site.name}</td>
              {DAY_LABELS.map((_, d) => {
                const passages = cells.get(`${site.id}|${d}`) || [];
                return (
                  <td key={d} style={{ ...td, background: d >= 5 ? '#fafafa' : undefined }}>
                    {passages.length === 0 ? (
                      <span style={{ color: '#e0e0e0' }}>·</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {passages.map((p, i) => (
                          <Tooltip
                            key={i}
                            title={`${p.dir === 'out' ? 'Pickup → ' : 'Delivery ← '}${p.other}${
                              p.window ? ' · ' + p.window : ''
                            }`}
                          >
                            <Tag
                              color={p.dir === 'out' ? 'green' : 'blue'}
                              style={{ margin: 0, fontSize: 11, whiteSpace: 'nowrap' }}
                            >
                              {p.dir === 'out' ? '→ ' : '← '}
                              {p.other}
                              {p.window ? <span style={{ opacity: 0.7 }}> {p.window}</span> : null}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
